import Scene from "./Scene";
import Rect from "./Rect";
import Vector2 from "./Vector2";
import NodeLayer from "./NodeLayer";
import ConnectionLayer from "./ConnectionLayer";
import Camera from "./Camera";

interface SerializedVec {
  x: number;
  y: number;
}

type LayerType = "NODE_LAYER" | "CONNECTION_LAYER" | "CAMERA_LAYER";

interface SerializedNodeLayer {
  position?: SerializedVec;
  size?: SerializedVec;
  question?: string;
  colorCode?: number;
  questionType?: string;
  choices?: Array<string>;
  narrativeTemplate?: string;
  startLayer?: boolean;
  finalMessage?: string;
  redirectUrl?: string;
  enabledName?: boolean;
  finalQuestion?: boolean;
  referralUrlStatus?: boolean;
  choicesStyles?: Array<string>;
  yesChoiceStyle?: string;
  noChoiceStyle?: string;
}

interface SerializedCameraLayer {
  position?: SerializedVec;
  scale: number;
}

interface SerializedConnectionLayer {
  startNodeId: string;
  endNodeId: string;
  condition: string;
}

interface SerializedObj {
  type?: LayerType;
  id?: string;
  data?:
    | SerializedNodeLayer
    | SerializedConnectionLayer
    | SerializedCameraLayer;
}

function deserializeNodeLayer(obj: SerializedObj, scene: Scene): NodeLayer {
  var data = obj.data as SerializedNodeLayer;
  var bbox = new Rect(
    new Vector2(data.position.x, data.position.y),
    new Vector2(data.size.x, data.size.y)
  );
  var layer = new NodeLayer(scene, obj.id, bbox);

  layer.setQuestionText(data.question);
  layer.setQuestionType(data.questionType);
  layer.setColorCode(data.colorCode);
  layer.setChoices(data.choices);
  layer.setNarrativeTemplate(data.narrativeTemplate);
  layer.setFinalMessage(data.finalMessage);
  layer.setFinalQuestion(data.finalQuestion);
  layer.setEnabledReferralUrl(data.referralUrlStatus);
  layer.setRedirectUrl(data.redirectUrl);
  layer.setEnabledName(data.enabledName);
  layer.setChoicesStyles(data.choicesStyles ?? []);
  layer.setYesChoiceStyle(data.yesChoiceStyle ?? "");
  layer.setNoChoiceStyle(data.noChoiceStyle ?? "");

  if (data.startLayer == true) {
    scene.setStartLayer(layer);
  }

  return layer;
}

function deserializeConnectionLayer(
  obj: SerializedObj,
  scene: Scene
): ConnectionLayer {
  var data = obj.data as SerializedConnectionLayer;
  var rootLayerGroup = scene.rootLayerGroup;

  var startNode = rootLayerGroup.findNodeLayerById(data.startNodeId);
  var endNode = rootLayerGroup.findNodeLayerById(data.endNodeId);

  if (startNode == null || endNode == null) {
    return null;
  }

  var connection = new ConnectionLayer(scene, obj.id, startNode);
  connection.setEndNode(endNode);
  connection.setCondition(data.condition);

  connection.startNode.addOutgoingConnection(connection);
  connection.endNode.addIncomingConnection(connection);
  return connection;
}

function deserializeCamera(obj: SerializedObj): Camera {
  var data = obj.data as SerializedCameraLayer;

  return new Camera(new Vector2(data.position.x, data.position.y), data.scale);
}

export function deserializeLayerGroup(str: string, scene: Scene) {
  // var buffer = new Buffer(str, "base64");
  var buffer = Buffer.from(str, "base64");
  var jsonStr = buffer.toString("utf-8");

  var arr: Array<SerializedObj> = JSON.parse(jsonStr);

  for (var obj of arr) {
    if (obj.type == "NODE_LAYER") {
      let desLayer = deserializeNodeLayer(obj, scene);
      if (desLayer != null) {
        scene.rootLayerGroup.addLayer(desLayer);
      }
    }
  }

  for (var obj of arr) {
    if (obj.type == "CONNECTION_LAYER") {
      let desLayer = deserializeConnectionLayer(obj, scene);
      if (desLayer != null) {
        scene.rootLayerGroup.addLayer(desLayer);
      }
    }
  }

  for (var obj of arr) {
    if (obj.type == "CAMERA_LAYER") {
      let desLayer = deserializeCamera(obj);
      if (desLayer != null) {
        scene.setCamera(desLayer);
      }
    }
  }
}

function serializeNodeLayer(
  layer: NodeLayer,
  startLayer: boolean
): SerializedObj {
  var ret: SerializedObj = {
    id: layer.id,
    type: "NODE_LAYER",
    data: {},
  };

  var data = ret.data as SerializedNodeLayer;

  var bbox = layer.boundingRect;

  data.position = {
    x: bbox.position.x,
    y: bbox.position.y,
  };
  data.size = {
    x: bbox.size.x,
    y: bbox.size.y,
  };
  data.question = layer.questionText;
  data.questionType = layer.questionType;
  data.colorCode = layer.colorCode;
  data.choices = layer.choices;
  data.narrativeTemplate = layer.narrativeTemplate;
  data.startLayer = startLayer;
  data.finalMessage = layer.finalMessage;
  data.finalQuestion = layer.finalQuestionStatus;
  data.referralUrlStatus = layer.referralUrlStatus;
  data.redirectUrl = layer.redirectUrl;
  data.enabledName = layer.enabledName;
  data.choicesStyles = layer.choicesStyles;
  data.yesChoiceStyle = layer.yesChoiceStyle;
  data.noChoiceStyle = layer.noChoiceStyle;

  return ret;
}

function serializeConnectionLayer(layer: ConnectionLayer): SerializedObj {
  var ret: SerializedObj = {
    id: layer.id,
    type: "CONNECTION_LAYER",
    data: {},
  };

  var data = ret.data as SerializedConnectionLayer;

  data.condition = layer.condition;
  data.startNodeId = layer.startNode.id;
  data.endNodeId = layer.endNode == null ? "" : layer.endNode.id;
  return ret;
}

function serializeCamera(camera: Camera): SerializedObj {
  var ret: SerializedObj = {
    id: "camera",
    type: "CAMERA_LAYER",
    data: {},
  };
  ret.data = {
    position: {
      x: camera.position.x,
      y: camera.position.y,
    },
    scale: camera.scale,
  };

  return ret;
}

export function serializeLayerGroup(scene: Scene): string {
  var ret = new Array<SerializedObj>();
  for (var layer of scene.rootLayerGroup.layers) {
    if (layer instanceof NodeLayer) {
      ret.push(serializeNodeLayer(layer, scene.startLayer == layer));
    }
  }

  for (var layer of scene.rootLayerGroup.layers) {
    if (layer instanceof ConnectionLayer) {
      ret.push(serializeConnectionLayer(layer));
    }
  }

  ret.push(serializeCamera(scene.camera));

  var dataUtf8 = JSON.stringify(ret);

  var buffer = Buffer.from(dataUtf8, "utf-8");
  var base64Str = buffer.toString("base64");

  return base64Str;
}
